import { useMutation } from '@tanstack/react-query';
import {
  Checkbox,
  Col,
  Flex,
  Form,
  Modal,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import axiosGraphQL from 'apis/GraphQL/axiosGraphQL';
import { QUERY_CREATE_DASHBOARD_TEAM_SALE } from 'apis/GraphQL/Queries/salePlan';
import { VARIABLE_CREATE_DASHBOARD_TEAM_SALE } from 'apis/GraphQL/Variables/salePlan';
import { PlantApi } from 'apis/PlantApis';
import SalePlanApi from 'apis/SalePlanApis';
import { SaleTeamApi } from 'apis/SaleTeamApi';
import { Calendar, CalendarBlue, ReloadBlue } from 'assets/svg';
import { Button, ButtonAction, SelectForm } from 'components';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import dayjs from 'dayjs';
import { ICreateSalePlanningAuto } from 'interfaces/SalePlanning';
import { IFilterTeamSaleCb } from 'interfaces/TeamSale/TeamSale';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import path from 'routes/Path';
import {
  formatCurrency,
  toastError,
  toastSuccess,
} from 'utils/constants/functions';
import convertToLabelValueCbb from 'utils/convertToLabelValueCbb';

const getTeamIdFromSubId = (data: any[], subId: string) => {
  return data.find((item) => item.value === subId)?.id;
};

export default function TeamDashboard() {
  const teamChangeRef = useRef<boolean>(false);
  const teamOptionRef = useRef<any>(true);
  const navigate = useNavigate();
  const { user } = useUser();
  const [searchParams] = useSearchParams();
  const { setTitle, setBreadcrumb } = useTitle();
  const [form] = useForm();
  const [optionPlants, setPlants] = useState<any>({ items: [] });
  const [dataDashboard, setDataDashboard] = useState<any>([]);
  const [isPending, setIsPending] = useState(true);
  const [isOpenModalDetail, setIsOpenModalDetail] = useState(false);
  const [isOnlyView, setIsOnlyView] = useState(false);
  const [itemDetail, setItemDetail] = useState<any>();
  const [isHover, setIsHover] = useState(false);
  const [teamSaleData, setTeamSaleData] =
    useState<ILazySelectData>(initLazySelectData);
  const loadingRef = useRef(false);

  const defaultFilter = useMemo(() => {
    const userAny = user as any;

    if (user?.isCN)
      return {
        plantId: userAny?.plantCode,
      };

    if (user?.isSale)
      return {
        plantId: userAny?.plantCode,
        teamSalesId: userAny?.teamSales?.[0]?.id,
        teamSalesSubId: userAny?.teamSales?.[0]?.subId,
      };

    return {};
  }, [user]);

  const teamId = searchParams.get('teamId');
  const year = Number(searchParams.get('year')) || dayjs().year();
  const month = Number(searchParams.get('month')) || dayjs().month() + 1;
  const plantId = searchParams.get('plantId');

  // setup calendar
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const firstDayOfWeek = dayjs(`${year}-${month}-01`).day();
  const daysInMonth = dayjs(`${year}-${month}-01`).daysInMonth();
  const daysInPreviousMonth = dayjs(`${year}-${month}-01`)
    .subtract(1, 'month')
    .daysInMonth();

  const mutationGetDashboard = useMutation({
    mutationFn: ({ teamSaleId }: { teamSaleId: string }) => {
      setIsPending(true);
      return SalePlanApi.getDashboardTeamSale({
        dayInMonth: daysInMonth,
        month,
        year,
        teamSaleId,
      });
    },
    onSuccess: (res) => {
      const items = res.data?.getDashboardSalesPlanning?.items;

      if (!items?.length) {
        setIsPending(false);
        return setDataDashboard([]);
      }

      const data = [...items]
        .sort((a: any, b: any) => {
          if (a.implementationDate !== b.implementationDate)
            return (
              new Date(a.implementationDate).getTime() -
              new Date(b.implementationDate).getTime()
            );

          return b.statusId.localeCompare(a.statusId);
        })
        .reduce((acc: any, item: any) => {
          const lastItem = acc[acc.length - 1];

          if (
            lastItem?.implementationDate &&
            item?.implementationDate &&
            dayjs(lastItem.implementationDate).get('date') ==
              dayjs(item.implementationDate).get('date')
          ) {
            Object.assign(lastItem, item);
            return [...acc.slice(0, acc.length - 1), { ...item }];
          }
          return [...acc, { ...item }];
        }, []);

      setDataDashboard(data);
      setIsPending(false);
    },
    onError: (res) => {
      console.log('onError', res);
    },
  });

  const { isPending: getTeamSaleLoading, mutateAsync: getTeamSales } =
    useMutation({
      mutationFn: (params: IFilterTeamSaleCb) =>
        SaleTeamApi.getAllTeamSaleCbSubSort(params),
      onSuccess: (res) => {
        if (res?.data?.getTeamSales) {
          const items = res.data?.getTeamSales?.items ?? [],
            totalItems = res.data?.getTeamSales?.pageInfo?.totalItems ?? 0;

          setTeamSaleData({
            options: convertToLabelValueCbb(items, {
              value: 'subId',
            }),
            totalItems: totalItems,
            hasNextPage: res.data?.getTeamSales?.pageInfo?.hasNextPage,
          });

          if (teamChangeRef.current) return;

          let subId;
          if (user?.isSale) subId = (user as any)?.teamSales?.[0]?.subId;
          const plantId = form.getFieldValue('plantId');

          if (teamId) {
            const item = items.find((item: any) => item.subId === teamId);
            subId = item?.subId;
          }

          if (!subId) {
            const item = items.find((item: any) => item.subId);
            subId = item?.subId;
          }
          form.setFieldValue('teamSaleId', subId);

          navigate(`${path.TEAM_DASHBOARD}?teamId=${subId}&plantId=${plantId}`);
        }
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const mutationCreateDashboard = useMutation({
    mutationFn: (params: ICreateSalePlanningAuto) =>
      axiosGraphQL.post('/graphql', {
        query: QUERY_CREATE_DASHBOARD_TEAM_SALE,
        variables: VARIABLE_CREATE_DASHBOARD_TEAM_SALE(params),
      }),
    onSuccess: (res) => {
      const teamId = getTeamIdFromSubId(
        teamSaleData.options,
        form.getFieldValue('teamSaleId'),
      );

      mutationGetDashboard.mutate({
        teamSaleId: teamId || defaultFilter.teamSalesId,
      });

      if (res.data?.generateSalesPlanning?.success)
        toastSuccess(res.data?.generateSalesPlanning?.message);
      else toastError(res.data?.generateSalesPlanning?.message);
    },
    onError: (res) => {
      console.log('onError', res);
      toastError(res.message || 'Có lỗi xảy ra');
    },
  });

  const { mutateAsync: getAllPlant, isPending: getAllPlantPending } =
    useMutation({
      mutationFn: () => PlantApi.getAllPlantComboBox(),
      onSuccess: (data: any) => {
        const items = data.data?.getPlant?.items || [];

        if (!items?.length) return;
        setPlants(() => ({
          items: items.map((item: any) => ({
            label: item.id,
            value: item.id,
          })),
          pageInfo: data.data.getPlant.pageInfo,
        }));
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const handleTeamSaleScrollChange = (
    params: ILazySelectParams & {
      plantId?: string;
    },
  ) => {
    const plantId = form.getFieldValue('plantId') || defaultFilter.plantId;
    if (!plantId) return;

    const teamSaleParams: IFilterTeamSaleCb = {
      ...params,
      plantId,
    };

    getTeamSales(teamSaleParams);
  };

  useEffect(() => {
    setTitle('Dashboard theo đội');
    setBreadcrumb([
      { title: 'Kế hoạch' },
      { title: 'Lập kế hoạch bán hàng', href: path.SALE_PLAN },
      { title: 'Dashboard theo đội' },
    ]);
  }, [setBreadcrumb, setTitle]);

  useEffect(() => {
    if (
      form.getFieldValue('teamSaleId') != undefined ||
      defaultFilter.teamSalesSubId
    ) {
      const teamId = getTeamIdFromSubId(
        teamSaleData.options,
        form.getFieldValue('teamSaleId'),
      );

      if (teamId || defaultFilter.teamSalesId)
        mutationGetDashboard.mutate({
          teamSaleId: teamId || defaultFilter.teamSalesId,
        });
    }
  }, [month, year]);

  useEffect(() => {
    const fetch = async () => {
      if (user?.isAdmin || user?.isHO) {
        // Handle Admin
        const plantData = await getAllPlant();
        const items = plantData?.data?.getPlant?.items || [];

        if (!items?.length) return;

        if (plantId && items.find((item: any) => item.id === plantId))
          form.setFieldValue('plantId', plantId);
        else form.setFieldValue('plantId', items[0].id);

        handleTeamSaleScrollChange({
          pageSize: 20,
        });
      } else if (defaultFilter.plantId) {
        // handle other role
        form.setFieldValue('plantId', defaultFilter.plantId);

        if (user?.isSale) {
          //
          form.setFieldValue('teamSaleId', defaultFilter.teamSalesSubId);

          if (
            teamId !== defaultFilter.teamSalesSubId ||
            plantId !== defaultFilter.plantId
          ) {
            navigate(
              `?teamId=${defaultFilter.teamSalesSubId}&plantId=${defaultFilter.plantId}`,
            );
          }

          mutationGetDashboard.mutate({
            teamSaleId: defaultFilter.teamSalesId,
          });
        } else
          handleTeamSaleScrollChange({
            pageSize: 20,
            value: '',
          });
      }
    };

    fetch();
  }, [user?.isHO, user?.isAdmin, defaultFilter.plantId, user?.isSale]);

  useEffect(() => {
    teamOptionRef.current = true;
  }, [teamId, plantId]);

  useEffect(() => {
    if (!teamId || !teamOptionRef.current) return;

    const teamSaleId = getTeamIdFromSubId(
      teamSaleData.options,
      form.getFieldValue('teamSaleId'),
    );

    if (!teamSaleId) return;

    mutationGetDashboard.mutate({
      teamSaleId,
    });
    form.setFieldsValue({
      teamSaleId: teamId,
    });
    teamOptionRef.current = false;
  }, [teamId, plantId, teamSaleData.options]);

  const onGotoNextMonth = () => {
    setDataDashboard([]);
    navigate(
      `${path.TEAM_DASHBOARD}?teamId=${teamId}&month=${
        month >= 12 ? 1 : month + 1
      }&year=${month >= 12 ? year + 1 : year}&plantId=${plantId}`,
    );
  };

  const onGotoPreviousMonth = () => {
    setDataDashboard([]);
    navigate(
      `${path.TEAM_DASHBOARD}?teamId=${teamId}&month=${
        month <= 1 ? 12 : month - 1
      }&year=${month <= 1 ? year - 1 : year}&plantId=${plantId}`,
    );
  };

  const onGotoToday = () => {
    // setDataDashboard([]);
    navigate(`${path.TEAM_DASHBOARD}?teamId=${teamId}&plantId=${plantId}`);
  };

  const RenderDay = (data: { key: any; item: any; type?: boolean }) => {
    const { key, item, type = true } = data;

    const expectedRevenue = item?.expectedRevenue;
    const renderExpectedValue =
      !Number.isNaN(Number(expectedRevenue)) &&
      formatCurrency({
        value: Number(expectedRevenue),
        round: true,
      });

    const teamCapacity: string = (item?.teamCapacity ?? '').toLowerCase();

    return (
      <Col
        key={key}
        className={`bg-white w-[calc(100%/7)] h-[150px] border border-solid border-[#E1E3E6] 
          p-2 ${type ? 'text-[#969696]' : 'text-[#96969660]'} text-base font-bold`}
        onClick={() => handleOpenModalDetail(item)}
      >
        {type ? (
          <Flex vertical justify="space-between" className="h-full pb-2">
            {dayjs(item.implementationDate).get('date')}
            {teamCapacity && (
              <Tag
                color={`${teamCapacity.startsWith('thiếu năng lực') ? 'red' : teamCapacity.startsWith('đủ năng lực') ? 'blue' : 'gold'}`}
                className={`w-max text-xs font-normal px-3 py-1`}
                style={{ borderRadius: '12px' }}
              >
                {item.teamCapacity}
              </Tag>
            )}
            <Typography.Text className="text-xs font-normal">
              {typeof renderExpectedValue === 'number'
                ? `Doanh thu dự kiến ${formatCurrency({
                    value: renderExpectedValue,
                    round: true,
                  })}`
                : null}
            </Typography.Text>
          </Flex>
        ) : (
          item
        )}
      </Col>
    );
  };

  const RenderWeekdays = () => {
    const listData = [];
    let index = 0;
    let dataDay = dataDashboard?.[0];

    for (let i = 0; i < firstDayOfWeek; i++) {
      listData.push(
        RenderDay({
          key: i * -1,
          item: daysInPreviousMonth - firstDayOfWeek + i + 1,
          type: false,
        }),
      );
    }

    for (let i = 1; i <= daysInMonth; i++) {
      if (dataDay && dayjs(dataDay?.implementationDate).get('date') == i) {
        listData.push(RenderDay({ key: i, item: dataDay }));

        index += 1;
        dataDay = dataDashboard?.[index];
      } else
        listData.push(
          RenderDay({
            key: i,
            item: { implementationDate: `${year}-${month}-${i}` },
          }),
        );
    }

    if ((daysInMonth + firstDayOfWeek) % 7 != 0)
      for (let i = 1; i <= 7 - ((daysInMonth + firstDayOfWeek) % 7); i++) {
        listData.push(
          RenderDay({ key: daysInMonth + i, item: i, type: false }),
        );
      }
    return listData;
  };

  function handleChangeTeamSale(value: any, option: any) {
    const plantId = form.getFieldValue('plantId');

    if (option && value) {
      const teamSaleId = getTeamIdFromSubId(
        teamSaleData.options,
        form.getFieldValue('teamSaleId'),
      );

      if (!teamSaleId) return;

      mutationGetDashboard.mutate({
        teamSaleId,
      });
      form.setFieldsValue({
        teamSaleId: value,
      });
      navigate(`${path.TEAM_DASHBOARD}?teamId=${value}&plantId=${plantId}`);
    } else teamChangeRef.current = true;
  }

  async function handleCreateDashboard() {
    if (loadingRef.current) return;
    loadingRef.current = true;

    const teamSalesId = getTeamIdFromSubId(
      teamSaleData.options,
      form.getFieldValue('teamSaleId'),
    );

    try {
      await mutationCreateDashboard.mutateAsync({
        month,
        year,
        team_id: defaultFilter.teamSalesId || teamSalesId,
        plantId: form.getFieldValue('plantId') || defaultFilter.plantId,
      });
    } catch (error) {
      // console.log('error', error);
    } finally {
      loadingRef.current = false;
    }
  }

  const handleCheckbox = (e: any) => {
    // console.log(`checked = ${e.target.checked}`);
    setIsOnlyView(e.target.checked);
  };

  const handleOpenModalDetail = (item?: any) => {
    if (isOnlyView || !item?.implementationDate) return;
    setIsOpenModalDetail(true);
    setItemDetail(item);
  };

  const handleMouseEnter = () => {
    setIsHover(true);
  };

  const handleMouseLeave = () => {
    setIsHover(false);
  };

  const handleChangePlant = (value: any) => {
    form.setFieldValue('plantId', value);
    form.setFieldValue('teamSaleId', undefined);

    handleTeamSaleScrollChange({
      pageSize: 20,
      value: '',
    });
    setDataDashboard([]);
  };

  useEffect(() => {
    defaultFilter.plantId && form.setFieldsValue(defaultFilter);
  }, [defaultFilter, form]);

  return (
    <Space direction="vertical" className="w-full pt-2">
      <Form form={form}>
        <Row gutter={[8, 8]} justify={'space-between'} align={'middle'}>
          <Col span={8}>
            <Row gutter={[8, 8]} align={'bottom'}>
              <Col>
                <Button title="<" handleOnclick={onGotoPreviousMonth} />
              </Col>
              <Col>
                <Button title=">" handleOnclick={onGotoNextMonth} />
              </Col>
              <Col>
                <Button title="Hôm nay" handleOnclick={onGotoToday} />
              </Col>
              <Col>
                <Checkbox onChange={handleCheckbox} checked={isOnlyView}>
                  Chỉ xem
                </Checkbox>
              </Col>
            </Row>
          </Col>

          <Col span={16}>
            <Row align={'middle'} gutter={[8, 8]} justify={'end'}>
              <Col style={{ fontSize: '24px', color: '#585757' }}>
                Tháng {month} năm {year}
              </Col>

              <Col>
                <Button
                  loading={loadingRef.current}
                  title="Tạo kế hoạch tự động"
                  iconBefore={isHover ? <Calendar /> : <CalendarBlue />}
                  handleOnclick={handleCreateDashboard}
                  handleOnMouseEnter={handleMouseEnter}
                  handleOnMouseLeave={handleMouseLeave}
                />
              </Col>

              <Col>
                <ButtonAction>
                  <ReloadBlue />
                </ButtonAction>
              </Col>

              <Col>
                <Form.Item name="plantId" className="mb-0 w-20">
                  <SelectForm
                    disabled={!(user?.isAdmin || user?.isHO)}
                    className="text-red"
                    loading={getAllPlantPending}
                    placeholder="Chọn chi nhánh"
                    options={optionPlants.items}
                    fieldNames={{
                      label: 'value',
                    }}
                    onChange={handleChangePlant}
                    allowClear={false}
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item
                  name={'teamSaleId'}
                  initialValue={teamId || undefined}
                  className="p-0 m-0"
                >
                  {/* <SelectForm
                    options={optionTeamSales}
                    onChange={handleChangeTeamSale}
                    allowClear={false}
                  /> */}
                  {user?.isSale ? (
                    <SelectForm
                      disabled
                      // loading={teamSalePending}
                      placeholder="Chọn đội"
                      options={
                        ((user as any)?.teamSales ?? []).map((item: any) => ({
                          label: item.subId + ' - ' + item.name,
                          value: item.subId,
                        })) ?? []
                      }
                    />
                  ) : (
                    <LazySelectForm
                      customeSize={20}
                      data={teamSaleData}
                      placeholder="Chọn đội"
                      loading={getTeamSaleLoading}
                      hasNextPage={teamSaleData.hasNextPage}
                      onSelectScrollChange={handleTeamSaleScrollChange}
                      onChange={handleChangeTeamSale}
                      onSelect={() => {
                        //
                      }}
                    />
                  )}
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>

        <Spin spinning={isPending || mutationCreateDashboard.isPending}>
          <Row justify={'center'} style={{ width: '100%', marginTop: '36px' }}>
            {weekdays.map((item, index) => {
              return (
                <Col
                  key={index}
                  className="bg-white w-[calc(100%/7)] border border-solid border-[#E1E3E6] p-2 text-[#484848] text-base font-normal"
                >
                  {item}
                </Col>
              );
            })}
            {RenderWeekdays()}
            {/* {listDataRender} */}
          </Row>
        </Spin>
        <Modal
          open={false} // FIXME
          title="Chi tiết"
          onCancel={() => setIsOpenModalDetail(false)}
          onOk={() => setIsOpenModalDetail(false)}
        >
          <div>
            {`Chi tiết ngày `}{' '}
            {dayjs(itemDetail?.implementationDate).get('date')}
          </div>
        </Modal>
      </Form>
    </Space>
  );
}
